import React, { useState } from 'react';
import { Container, Tab, Tabs } from 'react-bootstrap';
import { ProjectManager } from './components/ProjectManager';
import { GanttChart } from './components/GanttChart';
import { Project, Goal, Task, TimeEntry } from './types/models';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

function App() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeTab, setActiveTab] = useState<string>('projects');

  const handleProjectCreate = (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'goals'>) => {
    const newProject: Project = {
      ...projectData,
      id: Date.now().toString(),
      goals: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setProjects([...projects, newProject]);
  };

  const handleGoalCreate = (projectId: string, goalData: Omit<Goal, 'id' | 'tasks'>) => {
    setProjects(
      projects.map((project) => {
        if (project.id !== projectId) return project;
        const newGoal: Goal = {
          ...goalData,
          id: Date.now().toString(),
          tasks: [],
        };
        return { ...project, goals: [...project.goals, newGoal], updatedAt: new Date() };
      })
    );
  };

  const handleTaskCreate = (goalId: string, taskData: Omit<Task, 'id'>) => {
    setProjects(
      projects.map((project) => ({
        ...project,
        goals: project.goals.map((goal) => {
          if (goal.id !== goalId) return goal;
          const newTask: Task = {
            ...taskData,
            id: Date.now().toString(),
            timeEntries: [],
          };
          return { ...goal, tasks: [...goal.tasks, newTask] };
        }),
        updatedAt: new Date(),
      }))
    );
  };

  const handleTaskMove = (taskId: string, newGoalId: string, newStatus: Task['status'], newOrder: number) => {
    setProjects(
      projects.map((project) => {
        let movedTask: Task | null = null;
        const updatedGoals = project.goals.map((goal) => ({
          ...goal,
          tasks: goal.tasks.filter((task) => {
            if (task.id === taskId) {
              movedTask = { ...task, goalId: newGoalId, status: newStatus, order: newOrder };
              return false;
            }
            return true;
          }),
        }));
        if (!movedTask) return project;
        return {
          ...project,
          goals: updatedGoals.map((goal) => {
            if (goal.id !== newGoalId) return goal;
            const tasks = [...goal.tasks];
            tasks.splice(newOrder, 0, movedTask!);
            return { ...goal, tasks };
          }),
          updatedAt: new Date(),
        };
      })
    );
  };

  const handleAddTimeEntry = (taskId: string, entry: Omit<TimeEntry, 'id'>) => {
    const newEntry: TimeEntry = { ...entry, id: Date.now().toString() };
    setProjects(
      projects.map((project) => ({
        ...project,
        goals: project.goals.map((goal) => ({
          ...goal,
          tasks: goal.tasks.map((task) => {
            if (task.id !== taskId) return task;
            return { ...task, timeEntries: [...(task.timeEntries ?? []), newEntry] };
          }),
        })),
      }))
    );
  };

  const handleDeleteTimeEntry = (taskId: string, entryId: string) => {
    setProjects(
      projects.map((project) => ({
        ...project,
        goals: project.goals.map((goal) => ({
          ...goal,
          tasks: goal.tasks.map((task) => {
            if (task.id !== taskId) return task;
            return { ...task, timeEntries: (task.timeEntries ?? []).filter(e => e.id !== entryId) };
          }),
        })),
      }))
    );
  };

  return (
    <div className="App">
      <Container fluid>
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'projects')} className="mb-3">
          <Tab eventKey="projects" title="Projects">
            <ProjectManager
              projects={projects}
              onProjectCreate={handleProjectCreate}
              onGoalCreate={handleGoalCreate}
              onTaskCreate={handleTaskCreate}
              onProjectSelect={() => setActiveTab('gantt')}
            />
          </Tab>
          <Tab eventKey="gantt" title="Gantt Chart">
            <GanttChart
              projects={projects}
              onTaskMove={handleTaskMove}
              onAddTimeEntry={handleAddTimeEntry}
              onDeleteTimeEntry={handleDeleteTimeEntry}
            />
          </Tab>
        </Tabs>
      </Container>
    </div>
  );
}

export default App;
